// PotreeViewer3 리팩토링 - 베지어 제어점 드래그/수정 전용 훅
// 역할:
// - 수정 모드에서 베지어 제어점 선택/드래그 처리
// - Potree 카메라 컨트롤 lock/unlock
// - updateBezierCurve 호출로 베지어 곡선 실시간 갱신

import { useRef } from "react";
import * as THREE from "three";
import { updateBezierCurve } from "../util/bezierEditor";
import { getMinZ as getMinZFromViewer } from "../util/sceneObjects";

/**
 * useBezierDrag
 *
 * @param {any} potreeViewer
 * @param {boolean} editMode
 * @param {Array} bezierCurves
 * @param {Function} setBezierCurves
 *
 * @returns {{
 *   onMouseDown: (event: MouseEvent) => void,
 *   onMouseMove: (event: MouseEvent) => void,
 *   onMouseUp: () => void,
 * }}
 */
export function useBezierDrag(
  potreeViewer,
  editMode,
  bezierCurves,
  setBezierCurves
) {
  const selectedPointRef = useRef(null);
  const isDraggingRef = useRef(false);
  const dragPlaneRef = useRef(null);
  const raycasterRef = useRef(new THREE.Raycaster());
  const mouseRef = useRef(new THREE.Vector2());

  const getMinZ = () => getMinZFromViewer(potreeViewer);

  // 원본: PotreeViewer3.jsx onMouseDown
  const onMouseDown = (event) => {
    if (!editMode || !potreeViewer) return;

    // 포트리 뷰어 내부의 DOM 요소 참조
    const canvas = potreeViewer.renderer.domElement;
    const camera = potreeViewer.scene.getActiveCamera();
    const scene = potreeViewer.scene.scene;

    // 드래그 평면 생성
    const pointCloud = potreeViewer.scene.pointclouds[0];
    if (!pointCloud) return;

    const box = pointCloud.boundingBox
      .clone()
      .applyMatrix4(pointCloud.matrixWorld);
    const planeZ = box.min.z + 1;

    dragPlaneRef.current = new THREE.Plane(
      new THREE.Vector3(0, 0, 1),
      -planeZ
    );
    const rect = canvas.getBoundingClientRect();
    // 마우스 좌표를 정규화된 값으로 변환해 옮길 위치 파악
    mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    // 마우스 좌표를 카메라 좌표계로 변환해 옮길 위치 파악
    raycasterRef.current.setFromCamera(mouseRef.current, camera);

    let intersects = [];

    // 제어점 그룹 참조
    const controlGroup = scene.getObjectByName("bezier-controls");
    if (!controlGroup) return;

    // 제어점 그룹과 마우스 좌표 교차 확인
    intersects = raycasterRef.current.intersectObjects(
      controlGroup.children,
      true
    );

    if (intersects.length > 0) {
      const sphere = intersects[0].object;

      // 선택한 제어점 정보 저장
      if (sphere.userData.isControlPoint) {
        selectedPointRef.current = {
          sphere: sphere,
          pointIndex: sphere.userData.pointIndex,
          featureIndex: sphere.userData.featureIndex,
        };
        isDraggingRef.current = true;

        // 카메라 고정 로직
        if (potreeViewer?.controls) {
          potreeViewer.controls.enabled = false;
          potreeViewer.controls.update = () => {};
        }
        const sceneControls = potreeViewer?.scene?.controls;
        if (sceneControls) {
          sceneControls.enabled = false;
          sceneControls.update = () => {};
        }
        const orbitControls = potreeViewer?.scene?.orbitControls;
        if (orbitControls) {
          orbitControls.enabled = false;
          orbitControls.update = () => {};
        }
      }
    }
  };

  // 원본: PotreeViewer3.jsx onMouseMove
  const onMouseMove = (event) => {
    if (!isDraggingRef.current || !selectedPointRef.current || !potreeViewer) return;
    const canvas = potreeViewer.renderer.domElement;
    const camera = potreeViewer.scene.getActiveCamera();

    const rect = canvas.getBoundingClientRect();
    mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    raycasterRef.current.setFromCamera(mouseRef.current, camera);

    const intersectPoint = new THREE.Vector3();
    raycasterRef.current.ray.intersectPlane(
      dragPlaneRef.current,
      intersectPoint
    );

    const sp = selectedPointRef.current;
    if (intersectPoint) {
      // 제어점 실시간 업데이트
      sp.sphere.position.copy(intersectPoint);

      // 선택한 제어점 이동에 따른 베지어 곡선 업데이트
      updateBezierCurve(
        sp.featureIndex,
        sp.pointIndex,
        intersectPoint,
        setBezierCurves,
        potreeViewer,
        getMinZ
      );
    }
    // 카메라 반동 방지 로직
    if (potreeViewer.controls) {
      potreeViewer.controls.panDelta.set(0, 0);
      potreeViewer.controls.pitchDelta = 0;
      potreeViewer.controls.yawDelta = 0;
      potreeViewer.controls.radiusDelta = 0;
    }
  };

  // 원본: PotreeViewer3.jsx onMouseUp
  const onMouseUp = () => {
    if (!potreeViewer) return;
    if (isDraggingRef.current) {
      if (potreeViewer?.controls) {
        potreeViewer.controls.enabled = true;
        delete potreeViewer.controls.update;
      }
      const sceneControls = potreeViewer?.scene?.controls;
      if (sceneControls) {
        sceneControls.enabled = true;
        delete sceneControls.update;
      }
      const orbitControls = potreeViewer?.scene?.orbitControls;
      if (orbitControls) {
        orbitControls.enabled = true;
        delete orbitControls.update;
      }
      isDraggingRef.current = false;
      selectedPointRef.current = null;
    }
  };

  return {
    onMouseDown,
    onMouseMove,
    onMouseUp,
  };
}

